import { makeStyles } from '@material-ui/core/styles'
import { pageContentHorizontalGutters } from 'lib/styles/page-content-gutters'
import { borderedActionButtonStyles } from 'lib/styles/row-action-button'
import {
  STAT_TILE_BACKGROUND,
  statTileLabelStyles,
  statTileValueStyles,
} from 'lib/styles/stat-tile'

// Equal stat width keeps the results boxes aligned down the list.
const ROW_STAT_WIDTH = 88

const RESULTS_TWO_COLUMN_MIN_WIDTH = 1200

// Height of the fixed mobile action bar from the shared PageHeader
// (`mobileActionWrapper`), so the end of the page is never covered by it.
const MOBILE_ACTION_BAR_HEIGHT = 70

const STATUS_TONES = {
  DRAFT: { backgroundColor: '#F4F6F8', color: '#4F5B62' },
  SCHEDULED: { backgroundColor: '#E0EAFF', color: '#001C57' },
  SENT: { backgroundColor: '#BEF1BB', color: '#00210B' },
  CANCELED: { backgroundColor: '#F4F6F8', color: '#6B7C87' },
}

const useStyles = makeStyles(theme => ({
  pageContent: {
    ...pageContentHorizontalGutters(theme),
    margin: theme.spacing(0, 0, 12),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  content: {
    margin: '2rem auto 6rem',
    maxWidth: 832,
    padding: theme.spacing(0, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 1),
      marginTop: theme.spacing(2),
    },
  },
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: theme.spacing(2, 2.5),
    borderBottom: '1px solid #E0E0E0',
  },
  cardSubtitle: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  cardBody: {
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // Matches the left padding the avatar has inside rowLink.
    paddingRight: theme.spacing(2.5),
    color: 'inherit',
    '&:not(:last-child)': {
      borderBottom: '1px solid #E0E0E0',
    },
    '&:hover': {
      backgroundColor: '#FAFBFC',
    },
  },
  // The anchor's ::after covers the whole row so the row stays one click
  // target while the buttons and chip are siblings, not nested in the link.
  rowLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 0, 2, 2.5),
    textAlign: 'left',
    textDecoration: 'none',
    color: 'inherit',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
    },
  },
  rowActions: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  // The chip hugs the stats or actions beside it and sits flush against the
  // row's right padding.
  rowStatus: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexShrink: 0,
    // Row gap plus this margin equals the avatar-to-text gap in rowLink.
    marginLeft: theme.spacing(1),
    pointerEvents: 'none',
  },
  borderedActionButton: borderedActionButtonStyles(theme),
  // Doubled selector beats the theme's fixed contained-button height.
  sendActionButton: {
    '&&': {
      boxSizing: 'border-box',
      minWidth: 0,
      width: 32,
      height: 32,
      minHeight: 32,
      padding: 0,
      borderRadius: 8,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#172024',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowDetail: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  rowStats: {
    display: 'flex',
    alignItems: 'stretch',
    flexShrink: 0,
    padding: theme.spacing(1.25, 0),
    borderRadius: 12,
    backgroundColor: STAT_TILE_BACKGROUND,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  rowStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    width: ROW_STAT_WIDTH,
    boxSizing: 'border-box',
    padding: theme.spacing(0, 1),
    textAlign: 'center',
    '& + &': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  rowStatValue: statTileValueStyles,
  rowStatValueSuccess: {
    color: theme.palette.success.dark,
  },
  rowStatValueMuted: {
    color: theme.palette.text.secondary,
  },
  // Narrow enough that both labels wrap onto two lines so the stats line up.
  rowStatLabel: { ...statTileLabelStyles, maxWidth: 56 },
  statusBadge: ({ status }) => ({
    display: 'inline-block',
    flexShrink: 0,
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    ...(STATUS_TONES[status] || STATUS_TONES.DRAFT),
  }),
  pillButton: {
    borderRadius: 20,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  helperText: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  skillChip: {
    fontWeight: 500,
  },
  skillChipUnselected: {
    backgroundColor: 'transparent',
    border: '1px solid #70787C',
    color: '#40484C',
    '& .MuiChip-icon': {
      color: '#40484C',
    },
    '&:hover, &:focus': {
      backgroundColor: 'transparent',
    },
  },
  skillChipSelected: {
    backgroundColor: '#EBF0F2',
    border: '1px solid #EBF0F2',
    color: '#172024',
    '& .MuiChip-icon': {
      color: '#0053FF',
    },
    '&:hover, &:focus': {
      backgroundColor: '#EBF0F2',
    },
  },
  previewList: {
    maxHeight: 320,
    overflowY: 'auto',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
  },
  excluded: {
    color: theme.palette.text.disabled,
    textDecoration: 'line-through',
  },
  // One light grey for hover and keyboard focus; a selected option is shown by
  // its checkbox only.
  studentOptionRow: {
    minHeight: 56,
    boxSizing: 'border-box',
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover, &[data-focus="true"], &:active': {
      backgroundColor: '#F4F6F8',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-selected="true"]:hover, &[aria-selected="true"][data-focus="true"]': {
      backgroundColor: '#F4F6F8',
    },
  },
  filterOptionRow: {
    minHeight: 48,
    boxSizing: 'border-box',
    padding: theme.spacing(0.5, 1.5),
    '&:hover, &[data-focus="true"], &:active': {
      backgroundColor: '#F4F6F8',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-selected="true"]:hover, &[aria-selected="true"][data-focus="true"]': {
      backgroundColor: '#F4F6F8',
    },
  },
  optionLabel: {
    fontSize: '0.875rem',
    color: '#172024',
  },
  selectedChip: {
    margin: theme.spacing(0.25),
    '& .MuiChip-deleteIcon': {
      width: 16,
      height: 16,
      marginRight: theme.spacing(0.75),
      color: '#40484C',
      '&:hover': {
        color: '#172024',
      },
    },
  },
  studentRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 56,
    boxSizing: 'border-box',
    padding: theme.spacing(1, 1.5),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  studentRowLabel: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    '& .MuiFormControlLabel-label': {
      minWidth: 0,
    },
  },
  mediaGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  mediaTile: {
    position: 'relative',
    width: 112,
    height: 112,
    borderRadius: 8,
    border: '1px solid #E0E0E0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: '#FAFBFC',
    textAlign: 'center',
  },
  mediaImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mediaTitle: {
    fontSize: '0.75rem',
    lineHeight: 1.3,
    color: '#172024',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  mediaRemove: {
    position: 'absolute',
    top: 4,
    right: 4,
    padding: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  },
  mediaActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  actionBarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  fieldSection: {
    width: '100%',
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  sectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  sectionTitle: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  // The message is saved as markdown paragraphs and shown with a blank line
  // between them (preview and chat), so the editor spaces its paragraphs the
  // same way. Bullet lists (a <ul> of items) get the same gap around the list.
  messageEditor: {
    '& [data-contents="true"] > [data-block="true"]': {
      marginBottom: '1.2em',
    },
    '& [data-contents="true"] > ul': {
      margin: '0 0 1.2em',
    },
    '& [data-contents="true"] > [data-block="true"]:last-child, & [data-contents="true"] > ul:last-child': {
      marginBottom: 0,
    },
  },
  // The character counter sits alone under the editor, on the right.
  editorFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *': {
      margin: theme.spacing(0.75, 0, 0),
    },
  },
  sectionTitleWithHelper: {
    marginBottom: theme.spacing(0.5),
  },
  sectionHelper: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  dateTimeFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  listStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  chatPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    '& a': {
      pointerEvents: 'none',
    },
  },
  chatPreviewStart: {
    alignItems: 'flex-start',
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
  },
  chatBubble: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    boxSizing: 'border-box',
    width: 'min(100%, 375px)',
    padding: theme.spacing(0.5),
    borderRadius: '18px 18px 6px 18px',
    backgroundColor: '#E0EAFF',
    color: '#111111',
    fontSize: '0.9375rem',
    lineHeight: 1.35,
  },
  chatBubbleWithAttachments: {
    padding: theme.spacing(1),
  },
  // Details page only: fills the message column, or the card when stacked.
  chatBubbleStart: {
    width: 'min(100%, 640px)',
  },
  chatText: {
    overflowWrap: 'break-word',
    '& p': {
      margin: 0,
    },
    '& ul, & ol': {
      margin: 0,
      paddingLeft: theme.spacing(2.5),
    },
    '& a': {
      color: '#0053FF',
    },
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    padding: theme.spacing(0.75, 1.25),
  },
  // Details page only: long messages stop after about six lines.
  chatTextClamped: {
    maxHeight: 'calc(6 * 1.35em + 12px)',
    overflow: 'hidden',
  },
  // Whole image, never cropped; wide ones span the bubble and tall ones
  // letterbox on the bubble colour instead of growing the message.
  chatImage: {
    width: '100%',
    maxHeight: 280,
    objectFit: 'contain',
    borderRadius: 8,
    display: 'block',
  },
  // Details page only: a short banner crop instead of the full image.
  chatImageCompact: {
    maxHeight: 160,
    objectFit: 'cover',
  },
  chatFile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.common.white,
    border: '1px solid #E0E0E0',
    borderRadius: 8,
  },
  chatFileIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#EBF0F2',
    color: '#172024',
  },
  chatFileName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#172024',
  },
  // Message in a ~30% column beside the performance and students; stacked
  // (performance, students, message) below 1200px.
  resultsPage: {
    [theme.breakpoints.down('xs')]: {
      '&&': {
        paddingBottom: MOBILE_ACTION_BAR_HEIGHT + theme.spacing(3),
      },
    },
  },
  resultsLayout: {
    display: 'grid',
    alignItems: 'start',
    rowGap: theme.spacing(4),
    columnGap: theme.spacing(3),
    gridTemplateColumns: 'minmax(0, 1fr)',
    [theme.breakpoints.up(RESULTS_TWO_COLUMN_MIN_WIDTH)]: {
      gridTemplateColumns: 'minmax(0, 7fr) minmax(300px, 3fr)',
    },
  },
  resultsMain: {
    minWidth: 0,
  },
  resultsAside: {
    minWidth: 0,
    [theme.breakpoints.up(RESULTS_TWO_COLUMN_MIN_WIDTH)]: {
      position: 'sticky',
      top: theme.spacing(3),
    },
  },
  sectionHeading: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: theme.spacing(1.5),
  },
  sectionSubtitle: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  // Six equal neutral tiles: three across, two across on phones and while
  // the message column narrows the main column (1200-1439px).
  performanceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.up(RESULTS_TWO_COLUMN_MIN_WIDTH)]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.up(1440)]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
  },
  metricValue: {
    '&&': { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.15 },
  },
  infoButton: {
    padding: 2,
    fontSize: 16,
    color: theme.palette.text.secondary,
    '&:hover': { color: '#172024' },
  },
  breakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid #E0E0E0',
  },
  breakdownRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '12px',
    lineHeight: '18px',
  },
  breakdownDot: {
    flexShrink: 0,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#C4C7C9',
  },
  successDot: {
    backgroundColor: theme.palette.success.main,
  },
  breakdownLabel: {
    flex: 1,
    minWidth: 0,
    color: theme.palette.text.secondary,
  },
  breakdownValue: {
    fontWeight: 600,
    color: '#172024',
  },
  bookedStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Same 24px rhythm as the dialog title (the theme's DialogContent padding
  // would otherwise leave 8px under the bubble); doubled selector wins.
  fullMessageContent: {
    '&&': {
      padding: theme.spacing(1, 3, 3),
    },
  },
  viewFullMessage: {
    marginTop: theme.spacing(1),
    paddingLeft: 0,
  },
  tableShell: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  emptyPanel: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    padding: theme.spacing(5, 3),
    textAlign: 'center',
  },
  // Soft gray circle, as on the campaign list rows, with a dark icon.
  headerAvatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& .MuiSvgIcon-root, & svg': {
      width: 28,
      height: 28,
      color: '#172024',
      fill: 'currentColor',
    },
  },
  // Title block meta line, as under the student's name on Student details.
  headerMeta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  headerGrid: {
    // The shared header reserves a 67px back-button slot plus a 16px label
    // inset. Tighten it so the arrow sits on the content edge.
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '40px minmax(0, 1fr) auto',
      '& > :first-child': { width: 40 },
      '& > :nth-child(2)': { paddingLeft: theme.spacing(1.5) },
    },
    // On phones the arrow's icon sits on the content's left edge and the
    // title follows it closely.
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '32px minmax(0, 1fr) auto',
      '& > :first-child': { width: 32, justifyContent: 'flex-start' },
      '& > :first-child > *': { marginLeft: theme.spacing(-1) },
      '& > :nth-child(2)': { paddingLeft: theme.spacing(0.5) },
    },
  },
  recipientsTable: {
    // Containing block for the screen-reader-only text in empty cells, so it
    // can't widen the page.
    position: 'relative',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    '& table': {
      minWidth: 560,
      [theme.breakpoints.down('xs')]: {
        minWidth: 0,
        tableLayout: 'fixed',
      },
    },
    '& th': {
      whiteSpace: 'nowrap',
    },
  },
  // Phone rows: the last lesson under the student, indented under the name.
  compactLastLesson: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    paddingLeft: 48,
  },
  compactActivity: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  // Student cell opens that student's page. Plain table text; only the name
  // underlines, on hover or keyboard focus.
  studentLink: {
    display: 'block',
    minWidth: 0,
    color: 'inherit',
    textDecoration: 'none',
    outline: 'none',
    '&:hover $studentLinkName, &:focus-visible $studentLinkName': {
      textDecoration: 'underline',
    },
  },
  studentLinkName: {},
  recipientCaption: {
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
  },
  mutedValue: {
    color: '#C4C7C9',
  },
}))

export default useStyles
